import {
  DesignerSubscriptionType,
  PaymentFrequency,
  SinglePaymentItems,
} from "../__generated__/types";

const DESIGNER_HOME_PRODUCT_ID = "68b90b7678100b24b62b2b07";
const DESIGNER_PRODUCT_ID = "68cc4c575e0e65f9a5a83fc7";
const DESIGNER_PLUS_PRODUCT_ID = "68cc5fe1dc284fcf73c912a9";
const DESIGNER_SMART_GUILD_PRODUCT_ID =
  process.env.REACT_APP_HL_DESIGNER_SMART_GUILD_PRODUCT_ID ||
  process.env.REACT_APP_HL_DESIGN_PRODUCT_ID ||
  DESIGNER_PRODUCT_ID;

export const membershipTierPlanIds: Partial<
  Record<DesignerSubscriptionType, string>
> = {
  // Home (Beta) Plans
  [DesignerSubscriptionType.HomeBeta]: `${DESIGNER_HOME_PRODUCT_ID}-home-annually`,
  [DesignerSubscriptionType.HomeBiAnnuallyBeta]: `${DESIGNER_HOME_PRODUCT_ID}-home-bi-annually`,
  [DesignerSubscriptionType.HomeMonthly]: `${DESIGNER_HOME_PRODUCT_ID}-home-monthly`,
  [DesignerSubscriptionType.Home]: `${DESIGNER_HOME_PRODUCT_ID}-home-annually`,
  [DesignerSubscriptionType.HomeBiAnnually]: `${DESIGNER_HOME_PRODUCT_ID}-home-bi-annually`,

  // Design (Beta) Plans
  [DesignerSubscriptionType.DesignBeta]: `${DESIGNER_PRODUCT_ID}-design-annually`,
  [DesignerSubscriptionType.DesignBiAnnuallyBeta]: `${DESIGNER_PRODUCT_ID}-design-bi-annually`,
  [DesignerSubscriptionType.DesignMonthly]: `${DESIGNER_PRODUCT_ID}-design-monthly`,
  [DesignerSubscriptionType.Design]: `${DESIGNER_PRODUCT_ID}-design-annually`,
  [DesignerSubscriptionType.DesignBiAnnually]: `${DESIGNER_PRODUCT_ID}-design-bi-annually`,

  // Design+ (Beta) Plans
  [DesignerSubscriptionType.DesignPlusBeta]: `${DESIGNER_PLUS_PRODUCT_ID}-design+-annually`,
  [DesignerSubscriptionType.DesignPlusBiAnnuallyBeta]: `${DESIGNER_PLUS_PRODUCT_ID}-design+-bi-annually`,
  [DesignerSubscriptionType.DesignPlusMonthly]: `${DESIGNER_PLUS_PRODUCT_ID}-design+-monthly`,
  [DesignerSubscriptionType.DesignPlus]: `${DESIGNER_PLUS_PRODUCT_ID}-design+-annually`,
  [DesignerSubscriptionType.DesignPlusBiAnnually]: `${DESIGNER_PLUS_PRODUCT_ID}-design+-bi-annually`,

  // Smart Guild (Designer marketing membership)
  [DesignerSubscriptionType.Guild]: `${DESIGNER_SMART_GUILD_PRODUCT_ID}-smart-guild-annually`,
  [DesignerSubscriptionType.GuildBeta]: `${DESIGNER_SMART_GUILD_PRODUCT_ID}-smart-guild-annually`,
  [DesignerSubscriptionType.GuildBiAnnually]: `${DESIGNER_SMART_GUILD_PRODUCT_ID}-smart-guild-bi-annually`,
  [DesignerSubscriptionType.GuildBiAnnuallyBeta]: `${DESIGNER_SMART_GUILD_PRODUCT_ID}-smart-guild-bi-annually`,
  [DesignerSubscriptionType.GuildPlus]: `${DESIGNER_SMART_GUILD_PRODUCT_ID}-smart-guild-annually`,
  [DesignerSubscriptionType.GuildPlusBeta]: `${DESIGNER_SMART_GUILD_PRODUCT_ID}-smart-guild-annually`,
};

export const singePaymentItemIds = {
  [SinglePaymentItems.BioCreation]: "eab7d2b4-5f58-4fc4-a30d-03af93d24eed",
  [SinglePaymentItems.DesignerFilesFive]:
    "01c29525-f771-408f-b581-55c97cde8834",
  [SinglePaymentItems.DesignerFilesTen]: "d9ed1c7d-b6be-4bb7-a04f-8b72567366fb",
  [SinglePaymentItems.DesignerFilesFifteen]:
    "f0b21e74-a30b-452a-b412-1f4b3879696d",
  [SinglePaymentItems.Financing]: "bf741d77-b40b-4eec-8f2e-91d297dec303",
};

export const getMembershipTierFromPlanId = (planId: string, frequency?: PaymentFrequency) => {
  // Return undefined if planId is null, undefined, or empty
  if (!planId || planId === 'undefined' || planId.trim() === '') {
    return undefined;
  }

  // First try the exact match (for backward compatibility)
  const membershipTier = Object.entries(membershipTierPlanIds).find(
    ([_, id]) => id === planId
  );
  if (membershipTier) {
    return membershipTier[0];
  }

  // If no exact match, try to match by product ID and frequency
  // The planId from HighLevel is just the product ID, so we need to determine the tier
  // based on the product ID and frequency context
  if (planId === DESIGNER_HOME_PRODUCT_ID) {
    if (frequency === PaymentFrequency.Monthly) {
      return DesignerSubscriptionType.HomeMonthly;
    } else if (frequency === PaymentFrequency.BiAnnually) {
      return DesignerSubscriptionType.HomeBiAnnually;
    } else {
      return DesignerSubscriptionType.Home; // Annually
    }
  } else if (planId === DESIGNER_PRODUCT_ID) {
    if (frequency === PaymentFrequency.Monthly) {
      return DesignerSubscriptionType.DesignMonthly;
    } else if (frequency === PaymentFrequency.BiAnnually) {
      return DesignerSubscriptionType.DesignBiAnnually;
    } else {
      return DesignerSubscriptionType.Design; // Annually
    }
  } else if (planId === DESIGNER_PLUS_PRODUCT_ID) {
    if (frequency === PaymentFrequency.Monthly) {
      return DesignerSubscriptionType.DesignPlusMonthly;
    } else if (frequency === PaymentFrequency.BiAnnually) {
      return DesignerSubscriptionType.DesignPlusBiAnnually;
    } else {
      return DesignerSubscriptionType.DesignPlus; // Annually
    }
  } else if (planId === DESIGNER_SMART_GUILD_PRODUCT_ID) {
    if (frequency === PaymentFrequency.Monthly) {
      return DesignerSubscriptionType.Guild;
    } else if (frequency === PaymentFrequency.BiAnnually) {
      return DesignerSubscriptionType.GuildBiAnnually;
    } else {
      return DesignerSubscriptionType.Guild; // Annually/default
    }
  }

  console.warn("Unknown membership plan selected", planId, frequency);
  return undefined;
};

export const getPlanIdFromMembershipTier = (
  tier?: DesignerSubscriptionType | null
) => {
  if (!tier) {
    return undefined;
  }
  return membershipTierPlanIds[tier] ?? undefined;
};

export const getSinglePaymentItemFromId = (itemId: string) => {
  const singlePaymentItem = Object.entries(singePaymentItemIds).find(
    ([_, id]) => id === itemId
  );
  if (singlePaymentItem) {
    return singlePaymentItem[0];
  }
};

export const getPaymentFrequencyFromPlanId = (planId: string) => {
  const tier = getMembershipTierFromPlanId(planId);
  if (tier?.includes("BiAnnually")) {
    return PaymentFrequency.BiAnnually;
  } else if (tier?.includes("Monthly")) {
    return PaymentFrequency.Monthly;
  }
  return PaymentFrequency.Annually;
};
